import React, { useEffect } from 'react';
import { TrialInputProps } from '~/common/types';
import { Select } from '~/common/components/Select';
import { Input } from '~/common/components/Input';
import {
  validateString,
  validateEmail,
  validatePhone,
  validateSelect,
  validateFullName,
  validateRadio,
  validateZip,
} from '~/common/util/customValidation';
import { Field } from 'formik';
import { RadioFieldGroup } from '~/common/components/RadioFieldGroup';
import styles from '../Grid.module.css';
import { trackBlur } from '~/common/util/trackBlur';

export const MSPInput = (props: TrialInputProps) => {
  const {
    formikRef,
    formikTouched,
    formikErrors,
    formikValues,
    inputs,
    showHint,
    trackErrorBlur,
    validateBusinessEmail,
    existingAccount,
    existingEmailError,
  } = props;

  const { fullName, email, temp_phone__c, title, company, employees, country, zipCode, usersmanaged, states } = inputs;

  useEffect(() => {
    if (formikValues.country === 'US') {
      formikRef.current?.setFieldTouched('states', true);
      formikRef.current?.setFieldValue('states', 'AL');
    } else {
      formikRef.current?.setFieldTouched('states', false);
      formikRef.current?.setFieldValue('states', '');
    }
  }, [formikValues.country]);

  return (
    <div className={`${styles.inputGrid} ${styles.msp}`}>
      <Field
        name={fullName.id}
        type={'text'}
        error={formikErrors[fullName.id] && formikTouched[fullName.id]}
        errormessage={fullName.error}
        validate={validateFullName}
        as={Input}
        label={fullName.label}
        onFocus={() => {
          trackBlur(fullName.id);
        }}
        onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
          trackErrorBlur(fullName.id, validateFullName, event.target.value);
        }}
        autoFocus={true}
      />
      <Field
        name={email.id}
        type={'email'}
        error={formikErrors[email.id] && formikTouched[email.id]}
        errormessage={existingAccount ? existingEmailError : email.error}
        showHint={showHint}
        hintmessage={email.placeHolder}
        validate={validateEmail}
        as={Input}
        label={email.label}
        maxLength={128}
        onFocus={() => {
          trackBlur(email.id);
        }}
        onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
          validateBusinessEmail(event.target.value);
          trackErrorBlur(email.id, validateEmail, event.target.value);
        }}
      />
      <Field
        name={temp_phone__c.id}
        type={'tel'}
        error={formikErrors[temp_phone__c.id] && formikTouched[temp_phone__c.id]}
        errormessage={temp_phone__c.error}
        as={Input}
        label={temp_phone__c.label}
        validate={temp_phone__c.required ? validatePhone : ''}
        maxLength={30}
        onFocus={() => {
          trackBlur(temp_phone__c.id);
        }}
      />
      <Field
        name={title.id}
        type={'text'}
        error={formikErrors[title.id] && formikTouched[title.id]}
        errormessage={title.error}
        validate={validateString}
        as={Input}
        label={title.label}
        onFocus={() => {
          trackBlur(title.id);
        }}
        onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
          trackErrorBlur(title.id, validateString, event.target.value);
        }}
      />
      <Field
        name={company.id}
        type={'text'}
        error={formikErrors[company.id] && formikTouched[company.id]}
        errormessage={company.error}
        validate={validateString}
        as={Input}
        label={company.label}
        onFocus={() => {
          trackBlur(company.id);
        }}
        onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
          trackErrorBlur(company.id, validateString, event.target.value);
        }}
      />
      <Field
        name={country.id}
        as={Select}
        error={formikErrors[country.id] && formikTouched[country.id]}
        valid={formikValues[country.id] && formikValues[country.id] !== ''}
        errormessage={country.error}
        validate={validateSelect}
        required
        label={country.label}
        defaultValue={formikValues[country.id]}
        onBlur={(event: React.FocusEvent<HTMLSelectElement>) => {
          trackBlur(country.id);
          trackErrorBlur(country.id, validateSelect, event.target.value);
        }}
      >
        <option value="" hidden>
          {country.placeHolder}
        </option>
        {country?.options?.map((option: { [key: string]: string }, optionindex: number) => {
          const fieldName = Object.keys(option)[0] as string;
          return (
            <option key={optionindex.toString()} value={option[fieldName]}>
              {fieldName}
            </option>
          );
        })}
      </Field>
      <Field
        name={zipCode.id}
        type={'text'}
        error={formikErrors[zipCode.id] && formikTouched[zipCode.id]}
        errormessage={zipCode.error}
        validate={validateZip}
        as={Input}
        label={zipCode.label}
        onFocus={() => {
          trackBlur(zipCode.id);
        }}
        onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
          trackErrorBlur(zipCode.id, validateZip, event.target.value);
        }}
        max={12}
        min={3}
      />
      {formikValues.country === 'US' && (
        <Field
          name={states.id}
          as={Select}
          error={formikErrors[states.id] && formikTouched[states.id]}
          valid={formikValues[states.id] && formikValues[states.id] !== ''}
          errormessage={states.error}
          validate={validateSelect}
          required
          label={states.label}
          onBlur={(event: React.FocusEvent<HTMLSelectElement>) => {
            trackBlur(states.id);
            trackErrorBlur(states.id, validateSelect, event.target.value);
          }}
        >
          {states?.options?.map((option: { [key: string]: string }, optionindex: number) => {
            const fieldName = Object.keys(option)[0] as string;
            return (
              <option key={optionindex.toString()} value={option[fieldName]} selected={optionindex === 0}>
                {fieldName}
              </option>
            );
          })}
        </Field>
      )}
      <RadioFieldGroup
        name={employees.id}
        options={employees.options}
        label={employees.label}
        error={formikErrors[employees.id] && formikTouched[employees.id]}
        errorMessage={employees.error}
        onFocus={() => {
          trackBlur(employees.id);
        }}
        onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
          trackErrorBlur(employees.id, validateRadio, event.target.checked ? event.target.value : '');
        }}
      />
      <RadioFieldGroup
        name={usersmanaged.id}
        options={usersmanaged.options}
        label={usersmanaged.label}
        error={formikErrors[usersmanaged.id] && formikTouched[usersmanaged.id]}
        errorMessage={usersmanaged.error}
        onFocus={() => {
          trackBlur(usersmanaged.id);
        }}
        onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
          trackErrorBlur(usersmanaged.id, validateRadio, event.target.checked ? event.target.value : '');
        }}
      />
    </div>
  );
};
